import { Box, Button, Collapse, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Vector from "../../../../assets/images/svgs/VectorIcon.svg";
import AlertTriangle from "../../../../assets/images/svgs/alert-triangle.svg";
import { BackendError } from "../../../../config/axios";
import styles from "./FiboEditError.module.scss";

type Props = {
	className?: string;
	showDismissButton?: boolean;
	icon?: React.ReactNode;
	containerClassName?: string;
	error?: BackendError;
	onClear?: () => void;
};

const FiboEditError = ({ className, showDismissButton = false, icon, containerClassName, error, onClear }: Props) => {
	const { t, i18n } = useTranslation("translation", { keyPrefix: "playground.textToImage.errors" });

	const [open, setOpened] = useState(false);
	const [message, setMessage] = useState("");
	const [boldMessage, setBoldMessage] = useState("");
	const [errorStyle, setErrorStyle] = useState<"error" | "warning">("error");

	useEffect(() => {
		if (!error) {
			setOpened(false);
			setBoldMessage("");
			setMessage("");
			return;
		}

		// Determine error style based on status
		// If status is 500, use error style (red), otherwise use warning style (mustard)
		const isError = error.status === 500;
		setErrorStyle(isError ? "error" : "warning");

		const isErrorMessageExists = i18n.exists(`playground.textToImage.errors.${error.message}`);

		if (isErrorMessageExists) {
			setOpened(true);
			setMessage(t(`${error.message}`));

			// Set bold message for content moderation warnings
			if (error.status === 422 && error.message === "textToImagePlaygroundPromptContentModeration") {
				setBoldMessage(t("contentModerationErrorBoldMessage"));
			} else {
				setBoldMessage("");
			}
		} else if (error.message) {
			// Fallback for non-translated error messages
			setOpened(true);
			setMessage(error.message);
			setBoldMessage("");
		} else {
			setOpened(false);
			setBoldMessage("");
			setMessage("");
		}
	}, [error, t, i18n]);

	return (
		<Collapse in={open} onExited={onClear} className={containerClassName}>
			{errorStyle === "error" ? (
				<Box className={clsx(styles.errorMessageContainer, className)}>
					{icon || <img src={Vector} />}
					<Typography className={styles.errorMessage}>{message}</Typography>
				</Box>
			) : (
				<Box className={clsx(styles.warningMessageContainer, className)}>
					<>
						<Box className={styles.warningTextContainer}>
							{icon || <img src={AlertTriangle} />}
							<Typography className={styles.warningMessage}>
								{boldMessage && <span className={styles.warningBoldMessage}>{boldMessage}</span>}{" "}
								{message}
							</Typography>
						</Box>
						{showDismissButton && (
							<Button className={styles.warningDismissButton} onClick={onClear}>
								{t("dismissButton")}
							</Button>
						)}
					</>
				</Box>
			)}
		</Collapse>
	);
};

export default observer(FiboEditError);
